import type { UnwrapNestedRefs } from 'vue';
import type { MoviePlayerState } from 'src/models/moviePlayer';
import { logMoviePlaySeconds } from 'src/lib/analyticsHelper';
import { FULL_PLAYER_TYPE, PLAY_MSEC_SEND_THRES } from 'src/consts/movie_player';
import type { PlaybackControlContext } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerPlaybackControl';
import type { MovieTransitionContext } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerMovieTransition';

export interface TimeUpdateContext {
  onVjsTimeUpdate: (rawCurrentTime: number, duration: number) => void;
}

// video.jsのtimeupdateイベント処理（シークバー更新/mgi更新/動画末尾での自動遷移/analytics送信）を担うcomposable。
// mgi更新とmovieList遷移はmovieTransitionに、末尾到達時の一時停止はcontrol.pauseUIに委譲する。
export function useMoviePlayerTimeUpdate(
  state: UnwrapNestedRefs<MoviePlayerState>,
  control: Pick<PlaybackControlContext, 'pauseUI'>,
  movieTransition: Pick<MovieTransitionContext, 'playPreviousMovie' | 'playNextMovie' | 'trySetNextMovieGeoIndex'>,
): TimeUpdateContext {
  const onVjsTimeUpdate = (rawCurrentTime: number, duration: number) => {
    if (state.playback.isSeeking || state.movieList.current.isTryingToPlayAnotherMovie) return;

    if (!state.movieList.current.currentMovie) return;

    // update seekbar
    const currentTimeMsec = parseInt((rawCurrentTime * 1000).toString());
    const playOffsetDiff = currentTimeMsec - (state.movieList.current.currentMovie.playStartOffsetMsec || 0);
    state.playback.seek.current = (state.movieList.current.currentMovie.accumMsec || 0) + playOffsetDiff;
    state.playback.currentTime = rawCurrentTime;
    // 現在の再生位置が実際に撮影された時刻
    if (state.movieList.current.currentMovie.ts) {
      state.geo.currentMovieCurrentDt =
        new Date(state.movieList.current.currentMovie.ts.valueOf() + currentTimeMsec);
    }
    // mgiの更新
    if (state.geo.currentMgi?.endMsecDiff && state.geo.currentMgi.endMsecDiff <= currentTimeMsec) {
      movieTransition.trySetNextMovieGeoIndex();
    }
    // サーバ側で保持しているdurationはmtxをベースに記録しているが、動画ファイルそのもののdurationは
    // 若干誤差があったりするのでケア.
    if (!state.playback.videoControlProperties.isPlayBackward) {
      const endMsec = Math.floor(Math.min(duration * 1000, (state.movieList.current.currentMovie.hardEndOffsetMsec || 0)));
      if (endMsec <= currentTimeMsec) {
        if (state.movieList.current.isLastMovie) {
          // 終わり
          control.pauseUI();
          state.movieList.current.isMovieEnded = true;
        } else {
          // 次の動画を再生
          movieTransition.playNextMovie();
        }
      } else {
        state.movieList.current.isMovieEnded = false;
      }
    } else {
      const startMsec = Math.min(duration * 1000, (state.movieList.current.currentMovie.hardStartOffsetMsec || 0));
      if (startMsec >= currentTimeMsec) {
        if (state.movieList.current.isFirstMovie) {
          // 終わり
          control.pauseUI();
          state.movieList.current.isMovieEnded = true;
        } else {
          // 次の動画を再生
          movieTransition.playPreviousMovie();
        }
      } else {
        state.movieList.current.isMovieEnded = false;
      }
    }
    if (
      state.ui.analytics.tmpCalcBase === -1 ||
      currentTimeMsec < state.ui.analytics.tmpCalcBase
    ) {
      state.ui.analytics.tmpCalcBase = currentTimeMsec;
    }
    const playedMsec = currentTimeMsec - state.ui.analytics.tmpCalcBase;
    state.ui.analytics.accumulatedPlayMsecs += playedMsec;
    if (state.ui.analytics.accumulatedPlayMsecs >= PLAY_MSEC_SEND_THRES) {
      const seconds = parseInt((state.ui.analytics.accumulatedPlayMsecs / 1000).toString());
      logMoviePlaySeconds(FULL_PLAYER_TYPE, seconds);
      state.ui.analytics.accumulatedPlayMsecs = 0;
    }
    state.ui.analytics.tmpCalcBase = currentTimeMsec;
  };

  return {
    onVjsTimeUpdate,
  };
}
